import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ShoppingCart, DollarSign, TrendingUp, Calendar, Download, Eye,
  Search, Filter, ChevronDown, FileDown, BookOpen, User, Mail,
  Phone, Clock, Monitor, BarChart3, PieChart, ArrowUpRight, ArrowDownRight
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } });

// Stat Card Component
function StatCard({ title, value, subtitle, icon: Icon, trend, color = "cyan" }) {
  const colorClasses = {
    cyan: "bg-cyan-50 text-cyan-600 border-cyan-200",
    purple: "bg-purple-50 text-purple-600 border-purple-200",
    emerald: "bg-emerald-50 text-emerald-600 border-emerald-200",
    amber: "bg-amber-50 text-amber-600 border-amber-200"
  };

  return (
    <div className="bg-white border border-gray-200 p-5">
      <div className="flex items-start justify-between mb-3">
        <div className={`p-2.5 rounded-lg border ${colorClasses[color]}`}>
          <Icon size={20} />
        </div>
        {trend !== undefined && trend !== null && (
          <div className={`flex items-center gap-1 text-xs font-bold ${trend >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
            {trend >= 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
            {Math.abs(trend).toFixed(1)}%
          </div>
        )}
      </div>
      <h3 className="text-2xl font-outfit font-bold text-gray-900">{value}</h3>
      <p className="text-sm text-gray-500 font-dm-sans">{title}</p>
      {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
    </div>
  );
}

// Simple Bar Chart
function SimpleBarChart({ data, color = "#00F0FF" }) {
  const max = Math.max(...data.map(d => d.value || 0), 1);
  
  return (
    <div className="flex items-end gap-1 h-40">
      {data.map((item, i) => {
        const height = (item.value / max) * 100;
        return (
          <div key={i} className="flex-1 flex flex-col items-center gap-1">
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: `${height}%` }}
              transition={{ duration: 0.5, delay: i * 0.03 }}
              className="w-full rounded-t min-h-[2px]"
              style={{ backgroundColor: item.value > 0 ? color : '#e5e7eb' }}
              title={`${item.label}: ${item.value}`}
            />
            <span className="text-[9px] text-gray-400 truncate w-full text-center">
              {item.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// Horizontal Bar for Top Books
function HorizontalBar({ label, value, maxValue, color = "#00F0FF", secondaryValue }) {
  const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
  
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-gray-600 font-dm-sans truncate max-w-[200px]">{label}</span>
        <div className="flex items-center gap-2">
          <span className="text-gray-900 font-bold font-outfit">{value} sales</span>
          {secondaryValue !== undefined && (
            <span className="text-emerald-600 text-xs font-bold">₹{secondaryValue}</span>
          )}
        </div>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 0.6 }}
          className="h-full rounded-full"
          style={{ backgroundColor: color }}
        />
      </div>
    </div>
  );
}

// Purchase Row Component
function PurchaseRow({ purchase, onViewDetails }) {
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
      <td className="py-3 px-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
            <BookOpen size={18} className="text-purple-600" />
          </div>
          <div>
            <p className="font-dm-sans font-medium text-gray-900 text-sm">{purchase.book_title}</p>
            <p className="text-xs text-gray-500">{purchase.order_id}</p>
          </div>
        </div>
      </td>
      <td className="py-3 px-4">
        <div>
          <p className="font-dm-sans text-gray-900 text-sm">{purchase.buyer_name}</p>
          <p className="text-xs text-gray-500">{purchase.buyer_email}</p>
        </div>
      </td>
      <td className="py-3 px-4">
        <span className="font-outfit font-bold text-emerald-600">₹{purchase.amount_paid}</span>
        {purchase.discount_percentage > 0 && (
          <span className="ml-1 text-xs text-gray-400 line-through">₹{purchase.original_price}</span>
        )}
      </td>
      <td className="py-3 px-4">
        <span className={`px-2 py-1 text-xs font-bold rounded ${
          purchase.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
        }`}>
          {purchase.status === 'paid' ? 'Paid' : 'Pending'}
        </span>
      </td>
      <td className="py-3 px-4">
        <div className="flex items-center gap-3 text-xs text-gray-500">
          <div className="flex items-center gap-1" title="Downloads">
            <Download size={12} />
            <span>{purchase.download_count || 0}/5</span>
          </div>
          <div className="flex items-center gap-1" title="Devices">
            <Monitor size={12} />
            <span>{purchase.reader_devices?.length || 0}/2</span>
          </div>
        </div>
      </td>
      <td className="py-3 px-4 text-xs text-gray-500">
        {formatDate(purchase.paid_at || purchase.created_at)}
      </td>
      <td className="py-3 px-4">
        <button
          onClick={() => onViewDetails(purchase)}
          className="text-cyan-600 hover:text-cyan-700 text-sm font-medium"
        >
          Details
        </button>
      </td>
    </tr>
  );
}

// Purchase Details Modal
function PurchaseDetailsModal({ purchase, onClose, onResendEmail, onDiagnose, onFix }) {
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [diagnosing, setDiagnosing] = useState(false);
  const [diagnosis, setDiagnosis] = useState(null);
  const [fixing, setFixing] = useState(false);
  const [fixResult, setFixResult] = useState(null);
  
  if (!purchase) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { 
      day: '2-digit', month: 'short', year: 'numeric', 
      hour: '2-digit', minute: '2-digit' 
    });
  };
  
  const handleResendEmail = async () => {
    setResending(true);
    try {
      await onResendEmail(purchase.order_id);
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 3000);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to resend email");
    } finally {
      setResending(false);
    }
  };

  const handleDiagnose = async () => {
    setDiagnosing(true);
    setDiagnosis(null);
    try {
      const result = await onDiagnose(purchase.order_id);
      setDiagnosis(result);
    } catch (err) {
      setDiagnosis({ error: err.response?.data?.detail || "Diagnosis failed" });
    } finally {
      setDiagnosing(false);
    }
  };

  const handleFix = async () => {
    setFixing(true);
    setFixResult(null);
    try {
      const result = await onFix(purchase.order_id);
      setFixResult(result);
      // Re-diagnose after fix
      setTimeout(handleDiagnose, 1000);
    } catch (err) {
      setFixResult({ error: err.response?.data?.detail || "Fix failed" });
    } finally {
      setFixing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg max-w-lg w-full max-h-[80vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-outfit font-bold text-gray-900">Purchase Details</h2>
          <p className="text-sm text-gray-500">{purchase.order_id}</p>
        </div>
        
        <div className="p-6 space-y-4">
          {/* Book Info */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-bold text-gray-900 mb-2">{purchase.book_title}</h3>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-outfit font-bold text-emerald-600">₹{purchase.amount_paid}</span>
              {purchase.discount_percentage > 0 && (
                <>
                  <span className="text-gray-400 line-through">₹{purchase.original_price}</span>
                  <span className="bg-red-100 text-red-600 text-xs font-bold px-2 py-0.5 rounded">
                    {purchase.discount_percentage}% OFF
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Buyer Info */}
          <div className="space-y-2">
            <h4 className="font-bold text-gray-700 text-sm">Buyer Information</h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2 text-gray-600">
                <User size={14} />
                <span>{purchase.buyer_name}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Mail size={14} />
                <span>{purchase.buyer_email}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Phone size={14} />
                <span>{purchase.buyer_phone}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Clock size={14} />
                <span>{formatDate(purchase.paid_at || purchase.created_at)}</span>
              </div>
            </div>
          </div>

          {/* Access Info */}
          <div className="space-y-2">
            <h4 className="font-bold text-gray-700 text-sm">Access Information</h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-cyan-50 p-3 rounded-lg">
                <div className="flex items-center gap-2 text-cyan-700 mb-1">
                  <Download size={14} />
                  <span className="font-bold">Downloads</span>
                </div>
                <p className="text-cyan-900 text-lg font-outfit font-bold">
                  {purchase.download_count || 0} / 5
                </p>
              </div>
              <div className="bg-purple-50 p-3 rounded-lg">
                <div className="flex items-center gap-2 text-purple-700 mb-1">
                  <Monitor size={14} />
                  <span className="font-bold">Devices</span>
                </div>
                <p className="text-purple-900 text-lg font-outfit font-bold">
                  {purchase.reader_devices?.length || 0} / 2
                </p>
              </div>
            </div>
          </div>

          {/* Reading Progress */}
          {purchase.last_read_page && (
            <div className="space-y-2">
              <h4 className="font-bold text-gray-700 text-sm">Reading Progress</h4>
              <div className="bg-amber-50 p-3 rounded-lg">
                <p className="text-amber-800 text-sm">
                  Last read: Page {purchase.last_read_page}
                </p>
                <p className="text-amber-600 text-xs">
                  {formatDate(purchase.last_read_at)}
                </p>
              </div>
            </div>
          )}

          {/* Device History */}
          {purchase.reader_devices && purchase.reader_devices.length > 0 && (
            <div className="space-y-2">
              <h4 className="font-bold text-gray-700 text-sm">Device History</h4>
              <div className="space-y-2">
                {purchase.reader_devices.map((device, i) => (
                  <div key={i} className="bg-gray-50 p-3 rounded-lg text-xs">
                    <p className="text-gray-700 truncate">{device.user_agent?.substring(0, 60)}...</p>
                    <p className="text-gray-500 mt-1">
                      Registered: {formatDate(device.registered_at)} | 
                      Last access: {formatDate(device.last_access)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Payment Info */}
          <div className="space-y-2">
            <h4 className="font-bold text-gray-700 text-sm">Payment Details</h4>
            <div className="text-sm text-gray-600 space-y-1">
              <p><span className="font-medium">Order ID:</span> {purchase.order_id}</p>
              {purchase.payment_id && (
                <p><span className="font-medium">Payment ID:</span> {purchase.payment_id}</p>
              )}
              <p><span className="font-medium">Status:</span> 
                <span className={`ml-2 px-2 py-0.5 rounded text-xs font-bold ${
                  purchase.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                }`}>
                  {purchase.status === 'paid' ? 'Paid' : 'Pending'}
                </span>
              </p>
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-gray-200 space-y-2">
          {/* Diagnose & Fix Section */}
          <div className="space-y-2 mb-3">
            <div className="flex gap-2">
              <button
                onClick={handleDiagnose}
                disabled={diagnosing}
                className="flex-1 py-2 bg-amber-100 text-amber-700 font-bold hover:bg-amber-200 transition-colors flex items-center justify-center gap-2"
              >
                {diagnosing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-amber-700 border-t-transparent rounded-full animate-spin" />
                    Checking...
                  </>
                ) : (
                  <>🔍 Diagnose Issues</>
                )}
              </button>
              <button
                onClick={handleFix}
                disabled={fixing}
                className="flex-1 py-2 bg-purple-100 text-purple-700 font-bold hover:bg-purple-200 transition-colors flex items-center justify-center gap-2"
              >
                {fixing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-purple-700 border-t-transparent rounded-full animate-spin" />
                    Fixing...
                  </>
                ) : (
                  <>🔧 Auto-Fix</>
                )}
              </button>
            </div>
            
            {/* Diagnosis Results */}
            {diagnosis && (
              <div className={`p-3 rounded-lg text-xs ${diagnosis.is_healthy ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                <p className={`font-bold mb-2 ${diagnosis.is_healthy ? 'text-emerald-700' : 'text-red-700'}`}>
                  {diagnosis.is_healthy ? '✅ Purchase is healthy' : '⚠️ Issues found:'}
                </p>
                {diagnosis.purchase_issues?.length > 0 && (
                  <ul className="list-disc list-inside text-red-600 mb-2">
                    {diagnosis.purchase_issues.map((issue, i) => <li key={i}>{issue}</li>)}
                  </ul>
                )}
                {diagnosis.book_issues?.length > 0 && (
                  <ul className="list-disc list-inside text-red-600 mb-2">
                    {diagnosis.book_issues.map((issue, i) => <li key={i}>{issue}</li>)}
                  </ul>
                )}
                <div className="text-gray-600 mt-2 space-y-1">
                  <p><span className="font-medium">Download Token:</span> {diagnosis.download_token?.substring(0, 20)}...</p>
                  <p><span className="font-medium">Expires:</span> {diagnosis.download_expires}</p>
                  <p><span className="font-medium">Book has PDF:</span> {diagnosis.book_has_pdf ? 'Yes' : 'No'}</p>
                </div>
              </div>
            )}
            
            {/* Fix Results */}
            {fixResult && (
              <div className={`p-3 rounded-lg text-xs ${fixResult.error ? 'bg-red-50' : 'bg-emerald-50'}`}>
                <p className={`font-bold ${fixResult.error ? 'text-red-700' : 'text-emerald-700'}`}>
                  {fixResult.error || fixResult.message}
                </p>
                {fixResult.fixes_applied && (
                  <ul className="list-disc list-inside text-emerald-600 mt-1">
                    {fixResult.fixes_applied.map((fix, i) => <li key={i}>{fix}</li>)}
                  </ul>
                )}
              </div>
            )}
          </div>

          {purchase.status === 'paid' && (
            <button
              onClick={handleResendEmail}
              disabled={resending}
              className={`w-full py-2 font-bold transition-colors flex items-center justify-center gap-2 ${
                resendSuccess 
                  ? 'bg-emerald-100 text-emerald-700' 
                  : 'bg-cyan-100 text-cyan-700 hover:bg-cyan-200'
              }`}
            >
              {resending ? (
                <>
                  <div className="w-4 h-4 border-2 border-cyan-700 border-t-transparent rounded-full animate-spin" />
                  Sending...
                </>
              ) : resendSuccess ? (
                <>✓ Email Sent!</>
              ) : (
                <>📧 Resend Download Email</>
              )}
            </button>
          )}
          <button
            onClick={onClose}
            className="w-full py-2 bg-gray-100 text-gray-700 font-bold hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        </div>
      </motion.div>
    </div>
  );
}

export default function AdminBookPurchases() {
  const [activeTab, setActiveTab] = useState("purchases");
  const [purchases, setPurchases] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterBook, setFilterBook] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [books, setBooks] = useState([]);
  const [selectedPurchase, setSelectedPurchase] = useState(null);

  const resendEmail = async (orderId) => {
    await axios.post(`${API}/admin/book-purchases/${orderId}/resend-email`, {}, authHeader());
  };

  const diagnosePurchase = async (orderId) => {
    const res = await axios.get(`${API}/admin/book-purchases/${orderId}/diagnose`, authHeader());
    return res.data;
  };

  const fixPurchase = async (orderId) => {
    const res = await axios.post(`${API}/admin/book-purchases/${orderId}/fix`, {}, authHeader());
    return res.data;
  };

  const fetchData = async () => {
    try {
      const [purchasesRes, analyticsRes, booksRes] = await Promise.all([
        axios.get(`${API}/admin/book-purchases`, authHeader()),
        axios.get(`${API}/admin/book-purchases/analytics`, authHeader()),
        axios.get(`${API}/admin/books`, authHeader())
      ]);
      setPurchases(purchasesRes.data);
      setAnalytics(analyticsRes.data);
      setBooks(booksRes.data);
    } catch (err) {
      console.error("Failed to fetch data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // Filter purchases
  const filteredPurchases = purchases.filter(p => {
    const matchesSearch = !searchTerm || 
      p.buyer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.buyer_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.order_id?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesBook = !filterBook || p.book_id === filterBook;
    const matchesStatus = !filterStatus || p.status === filterStatus;
    return matchesSearch && matchesBook && matchesStatus;
  });

  // Export to CSV
  const exportToCSV = () => {
    const headers = ["Order ID", "Book Title", "Buyer Name", "Buyer Email", "Buyer Phone", "Amount", "Status", "Purchase Date", "Downloads"];
    const rows = filteredPurchases.map(p => [
      p.order_id,
      p.book_title,
      p.buyer_name,
      p.buyer_email,
      p.buyer_phone,
      p.amount_paid,
      p.status,
      p.paid_at || p.created_at,
      p.download_count || 0
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `book-purchases-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-outfit font-bold text-gray-900">Document Purchases</h1>
          <p className="text-gray-500 font-dm-sans">Track sales and analyze revenue</p>
        </div>
        <button
          onClick={exportToCSV}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 font-bold hover:bg-gray-200 transition-colors"
        >
          <FileDown size={18} />
          Export CSV
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Total Sales"
          value={analytics?.total_sales || 0}
          icon={ShoppingCart}
          color="cyan"
        />
        <StatCard
          title="Total Revenue"
          value={`₹${analytics?.total_revenue?.toLocaleString() || 0}`}
          icon={DollarSign}
          color="emerald"
        />
        <StatCard
          title="Today's Sales"
          value={analytics?.today_sales || 0}
          subtitle={`₹${analytics?.today_revenue || 0}`}
          icon={Calendar}
          color="purple"
        />
        <StatCard
          title="This Month"
          value={analytics?.month_sales || 0}
          subtitle={`₹${analytics?.month_revenue?.toLocaleString() || 0}`}
          icon={TrendingUp}
          color="amber"
          trend={analytics?.month_growth}
        />
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex gap-4">
          <button
            onClick={() => setActiveTab("purchases")}
            className={`pb-3 px-1 font-bold text-sm transition-colors border-b-2 ${
              activeTab === "purchases" 
                ? "border-cyan-500 text-cyan-600" 
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <span className="flex items-center gap-2">
              <ShoppingCart size={16} />
              All Purchases
            </span>
          </button>
          <button
            onClick={() => setActiveTab("analytics")}
            className={`pb-3 px-1 font-bold text-sm transition-colors border-b-2 ${
              activeTab === "analytics" 
                ? "border-cyan-500 text-cyan-600" 
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <span className="flex items-center gap-2">
              <BarChart3 size={16} />
              Analytics
            </span>
          </button>
        </div>
      </div>

      {/* Purchases Tab */}
      {activeTab === "purchases" && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-3">
            <div className="flex-1 min-w-[200px] relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, or order ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 text-sm focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              />
            </div>
            <select
              value={filterBook}
              onChange={(e) => setFilterBook(e.target.value)}
              className="px-4 py-2 border border-gray-200 text-sm bg-white"
            >
              <option value="">All Documents</option>
              {books.map(b => (
                <option key={b.id} value={b.id}>{b.title}</option>
              ))}
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-200 text-sm bg-white"
            >
              <option value="">All Status</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
            </select>
          </div>

          {/* Purchases Table */}
          {filteredPurchases.length === 0 ? (
            <div className="text-center py-12 bg-white border border-gray-200">
              <ShoppingCart size={48} className="mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-outfit font-bold text-gray-900 mb-2">No Purchases Found</h3>
              <p className="text-gray-500 font-dm-sans">
                {purchases.length === 0 
                  ? "No document purchases yet" 
                  : "No purchases match your filters"}
              </p>
            </div>
          ) : (
            <div className="bg-white border border-gray-200 overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-bold text-gray-500 uppercase">Book</th>
                    <th className="text-left py-3 px-4 text-xs font-bold text-gray-500 uppercase">Buyer</th>
                    <th className="text-left py-3 px-4 text-xs font-bold text-gray-500 uppercase">Amount</th>
                    <th className="text-left py-3 px-4 text-xs font-bold text-gray-500 uppercase">Status</th>
                    <th className="text-left py-3 px-4 text-xs font-bold text-gray-500 uppercase">Usage</th>
                    <th className="text-left py-3 px-4 text-xs font-bold text-gray-500 uppercase">Date</th>
                    <th className="text-left py-3 px-4 text-xs font-bold text-gray-500 uppercase"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPurchases.map((purchase) => (
                    <PurchaseRow 
                      key={purchase.id} 
                      purchase={purchase} 
                      onViewDetails={setSelectedPurchase}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="text-sm text-gray-500">
            Showing {filteredPurchases.length} of {purchases.length} purchases
          </p>
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === "analytics" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Sales Trend */}
          <div className="bg-white border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-outfit font-bold text-gray-900">Sales Trend (Last 14 Days)</h3>
              <TrendingUp size={18} className="text-gray-400" />
            </div>
            {analytics?.daily_sales?.length > 0 ? (
              <SimpleBarChart 
                data={analytics.daily_sales.map(d => ({ label: d.date, value: d.sales }))} 
                color="#00F0FF" 
              />
            ) : (
              <div className="h-40 flex items-center justify-center text-gray-400">No data yet</div>
            )}
          </div>

          {/* Revenue Trend */}
          <div className="bg-white border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-outfit font-bold text-gray-900">Revenue Trend (Last 14 Days)</h3>
              <DollarSign size={18} className="text-gray-400" />
            </div>
            {analytics?.daily_sales?.length > 0 ? (
              <SimpleBarChart 
                data={analytics.daily_sales.map(d => ({ label: d.date, value: d.revenue }))} 
                color="#10B981" 
              />
            ) : (
              <div className="h-40 flex items-center justify-center text-gray-400">No data yet</div>
            )}
          </div>

          {/* Top Selling Documents */}
          <div className="bg-white border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-outfit font-bold text-gray-900">Top Selling Documents</h3>
              <BookOpen size={18} className="text-gray-400" />
            </div>
            <div className="space-y-3">
              {analytics?.top_books?.length > 0 ? (
                analytics.top_books.map((book, i) => (
                  <HorizontalBar
                    key={i}
                    label={book.title}
                    value={book.sales}
                    maxValue={analytics.top_books[0]?.sales || 1}
                    color="#8B5CF6"
                    secondaryValue={book.revenue}
                  />
                ))
              ) : (
                <div className="text-center text-gray-400 py-4">No sales yet</div>
              )}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="bg-white border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-outfit font-bold text-gray-900">Quick Stats</h3>
              <PieChart size={18} className="text-gray-400" />
            </div>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600 text-sm">Average Order Value</span>
                <span className="font-outfit font-bold text-gray-900">
                  ₹{analytics?.avg_order_value?.toFixed(0) || 0}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600 text-sm">Total Downloads</span>
                <span className="font-outfit font-bold text-gray-900">
                  {analytics?.total_downloads || 0}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600 text-sm">Active Readers</span>
                <span className="font-outfit font-bold text-gray-900">
                  {analytics?.active_readers || 0}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600 text-sm">Repeat Buyers</span>
                <span className="font-outfit font-bold text-gray-900">
                  {analytics?.repeat_buyers || 0}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Purchase Details Modal */}
      {selectedPurchase && (
        <PurchaseDetailsModal 
          purchase={selectedPurchase} 
          onClose={() => setSelectedPurchase(null)}
          onResendEmail={resendEmail}
          onDiagnose={diagnosePurchase}
          onFix={fixPurchase}
        />
      )}
    </div>
  );
}
